import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { authApi } from '../lib/api'

const AuthContext = createContext(null)

function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshMe = useCallback(async () => {
    try {
      const me = await authApi.me()
      setUser(me)
      return me
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        setUser(null)
        return null
      }
      throw error
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const run = async () => {
      try {
        const me = await authApi.me()
        if (mounted) setUser(me)
      } catch (error) {
        if (mounted) setUser(null)
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    run()

    return () => {
      mounted = false
    }
  }, [])

  const login = useCallback(async (username, password) => {
    await authApi.login({ username, password })
    const me = await authApi.me()
    setUser(me)
    return me
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      setUser(null)
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: Boolean(user),
      login,
      logout,
      refreshMe,
      setUser
    }),
    [user, isLoading, login, logout, refreshMe]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }

  return context
}

export { AuthProvider, useAuth }
